import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  Truck,
  ChefHat,
  PartyPopper,
  Heart,
  Trees,
  UtensilsCrossed,
  ArrowRight,
  Download,
} from 'lucide-react';

const HizmetlerSection = ({ scrollToSection }) => {
  const { t } = useLanguage();

  const mainServices = [
    {
      key: 'tasimaYemek',
      icon: <Truck className="w-8 h-8" />,
      contentKey: 'tasimaContent',
    },
    {
      key: 'yerindeUretim',
      icon: <ChefHat className="w-8 h-8" />,
      contentKey: 'yerindeContent',
    },
  ];

  const organizationServices = [
    {
      key: 'kardelenDugun',
      icon: <Heart className="w-6 h-6" />,
      contentKey: 'kardelenContent',
    },
    {
      key: 'laVitaGarden',
      icon: <Trees className="w-6 h-6" />,
      contentKey: 'laVitaContent',
    },
    {
      key: 'outsideCatering',
      icon: <UtensilsCrossed className="w-6 h-6" />,
      contentKey: 'outsideContent',
    },
  ];

  return (
    <section id="hizmetler" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            {t('services.title')}
          </h2>
          <p className="text-xl text-gray-600 mb-4">{t('services.subtitle')}</p>
          <div className="w-24 h-1 bg-amber-500 mx-auto"></div>
        </div>

        {/* Main Services */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {mainServices.map((service) => (
            <Card
              key={service.key}
              className="border-none shadow-xl hover:shadow-2xl transition-all group overflow-hidden"
            >
              <CardHeader className="bg-gradient-to-r from-amber-500 to-amber-600 text-white p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    {service.icon}
                  </div>
                  <CardTitle className="text-2xl">
                    {t(`services.${service.key}`)}
                  </CardTitle>
                </div>
              </CardHeader>

              <CardContent className="p-6">
                <p className="text-gray-600 mb-6">
                  {t(`services.${service.contentKey}`)}
                </p>

                {/* Images */}
                <div className="grid grid-cols-3 gap-3">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center hover:bg-amber-50 transition-colors cursor-pointer group/img"
                    >
                      <img
                        src={`/${service.key}-${i}.jpg`}
                        alt={`${t(`services.${service.key}`)} ${i}`}
                        className="w-full h-full object-cover rounded-lg"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = `
                            <div class="flex flex-col items-center text-gray-400 group-hover/img:text-amber-500 transition-colors">
                              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <rect width="18" height="18" x="3" y="3" rx="2" ry="2"/>
                                <circle cx="9" cy="9" r="2"/>
                                <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/>
                              </svg>
                              <span class="text-xs mt-1">Görsel</span>
                            </div>
                          `;
                        }}
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Davet & Organizasyon */}
        <Card className="border-none shadow-xl mb-12">
          <CardHeader className="bg-gradient-to-r from-gray-800 to-gray-700 text-white p-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-amber-500/20 flex items-center justify-center">
                <PartyPopper className="w-8 h-8 text-amber-400" />
              </div>
              <CardTitle className="text-2xl">{t('services.davetOrganizasyon')}</CardTitle>
            </div>
          </CardHeader>

          <CardContent className="p-8">
            <div className="grid md:grid-cols-3 gap-8">
              {organizationServices.map((service) => (
                <div key={service.key} className="group">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 group-hover:bg-amber-500 group-hover:text-white transition-colors">
                      {service.icon}
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">
                      {t(`services.${service.key}`)}
                    </h3>
                  </div>

                  <p className="text-gray-600 mb-4">{t(`services.${service.contentKey}`)}</p>

                  {/* Images */}
                  <div className="grid grid-cols-3 gap-2">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center hover:bg-amber-50 transition-colors cursor-pointer"
                      >
                        <img
                          src={`/${service.key}-${i}.jpg`}
                          alt={`${t(`services.${service.key}`)} ${i}`}
                          className="w-full h-full object-cover rounded-lg"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML =
                              '<div class="flex flex-col items-center text-gray-400"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg></div>';
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button
            size="lg"
            className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all group"
            onClick={() => scrollToSection('iletisim')}
          >
            {t('services.btnTeklif')}
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>

          <Button
            size="lg"
            variant="outline"
            className="border-2 border-gray-800 text-gray-800 hover:bg-gray-800 hover:text-white font-semibold px-8 py-6 text-lg transition-all"
            onClick={() => window.open('/e-katalog.pdf', '_blank')}
          >
            <Download className="mr-2 w-5 h-5" />
            {t('services.btnKatalog')}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default HizmetlerSection;